import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CURRENCY_SYMBOLS } from '../utils/currencySymbols';

const BarChartStructure = ({ 
  data, 
  dataKeys = [],
  xAxisKey = "name",
  barColor = "#818CF8",
  title,
  xAxisLabel,
  yAxisLabel,
  currencySymbol = '£'  // Default currency symbol
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white rounded-lg p-4">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Array of colors for different currencies
  const colors = ['#818CF8', '#F87171', '#34D399', '#FBBF24', '#A78BFA', '#F472B6', '#60A5FA'];

  // Custom tooltip component to format the value with 2 decimal places
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
          <p className="font-medium text-sm">{label}</p>
          <div className="space-y-1 mt-1">
            {payload.map((entry, index) => {
              // If we're dealing with multiple currencies, use the name as the currency code
              // For the Expense page, dataKeys will be currency codes like "GBP", "USD", etc.
              const symbol = dataKeys.length > 1 ? CURRENCY_SYMBOLS[entry.name] || '£' : currencySymbol;
              
              return (
                <div key={index} className="flex items-center text-xs">
                  <div
                    className="w-3 h-3 mr-2"
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="mr-2">{entry.name}:</span>
                  <span className="font-medium">{symbol}{Number(entry.value).toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
      {title && (
        <h3 className="text-lg font-semibold mb-4 text-center">{title}</h3>
      )}
      <div className="h-[350px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 25 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={xAxisKey} 
              label={xAxisLabel ? { 
                value: xAxisLabel, 
                position: 'bottom' 
              } : null}
            />
            <YAxis
              label={yAxisLabel ? { 
                value: yAxisLabel, 
                angle: -90, 
                position: 'insideLeft' 
              } : null}
            />
            <Tooltip content={<CustomTooltip />} />
            {dataKeys.map((key, index) => (
              <Bar 
                key={key}
                dataKey={key}
                fill={colors[index % colors.length]}
                name={key}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChartStructure; 